import React, { FC, memo, useEffect, useMemo } from 'react'

import { useFormContext } from 'react-hook-form'

import { CustomerAddress } from 'Types'

import ErrorMessage from 'Components/ErrorMessage'
import FormErrors from 'Components/FormErrors'

import AddressSelect from './AddressSelect'
import CitySelect from './CitySelect'
import CountrySelector from './CountrySelect'
import StateSelect from './StateSelect'

import './antd.scss'

export interface FormAddress {
  address1: string
  address2: string
  city: string
  cityGooglePlaceId: string
  countryIsoCode: string
  firstName: string
  lastName: string
  latitude: number
  longitude: number
  state?: string
  stateIsoCode: string
  zipCode: string
}

interface Props {
  calcTransportCost(): void
  cssPrefix: string
  /**
   * Forwarded to every antd <Select> here. Defaults to antd's own
   * (document.body) when omitted — fine everywhere these fields render in
   * normal document flow (e.g. Checkout). Inside a Radix Dialog, though,
   * antd's body-portalled dropdown sits outside Dialog.Content's DOM
   * subtree, so Radix's outside-click/focus-trap logic treats clicks on it
   * as "outside" and swallows them before the option's onSelect fires —
   * pass a container inside the dialog to keep the dropdown a descendant
   * of Dialog.Content.
   */
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement
  googlePlacesApiKey: string
  isUSLocation?: boolean
  nameGroupPrefix: string
  prefillName?: { firstName?: string | null; lastName?: string | null } | null
}

export const FIELDS = [
  'firstName',
  'lastName',
  'address1',
  'address2',
  'city',
  'cityGooglePlaceId',
  'countryIsoCode',
  'stateIsoCode',
  'zipCode',
  'phone',
  'latitude',
  'longitude',
] as (keyof CustomerAddress)[]

const AddressFields: FC<Props> = ({
  nameGroupPrefix,
  cssPrefix,
  calcTransportCost,
  getPopupContainer,
  googlePlacesApiKey,
  isUSLocation = false,
  prefillName,
}) => {
  const { register, unregister, setValue, errors } = useFormContext()
  const subFormErrors = errors[nameGroupPrefix]
  const fieldNames = useMemo(() => FIELDS.map((field) => `${nameGroupPrefix}[${field}]`), [nameGroupPrefix])

  const countryFieldName = `${nameGroupPrefix}[countryIsoCode]`

  useEffect(() => {
    setValue(`${nameGroupPrefix}[firstName]`, prefillName?.firstName)
    setValue(`${nameGroupPrefix}[lastName]`, prefillName?.lastName)
  }, [nameGroupPrefix, setValue, prefillName])

  useEffect(() => {
    return () => unregister(fieldNames)
  }, [unregister, fieldNames])

  return (
    <div className="row" data-address-type={nameGroupPrefix}>
      <div className="col-md-6">
        <div className="form-group">
          <label htmlFor={`${cssPrefix}-first-name`}>First Name</label>
          <input
            ref={register({ required: 'First name is required' })}
            className="form-control"
            id={`${cssPrefix}-first-name`}
            name={`${nameGroupPrefix}[firstName]`}
            placeholder="Enter your first name..."
            type="text"
          />
          <ErrorMessage as={FormErrors} errors={subFormErrors} name="firstName" />
        </div>
      </div>
      <div className="col-md-6">
        <div className="form-group">
          <label htmlFor={`${cssPrefix}-last-name`}>Last Name</label>
          <input
            ref={register({ required: 'Last name is required' })}
            className="form-control"
            id={`${cssPrefix}-last-name`}
            name={`${nameGroupPrefix}[lastName]`}
            placeholder="Enter your last name..."
            type="text"
          />
          <ErrorMessage as={FormErrors} errors={subFormErrors} name="lastName" />
        </div>
      </div>

      <div className="col-md-12">
        <div className="form-group">
          <label htmlFor={`${cssPrefix}-address-1`}>Address</label>
          <AddressSelect
            calcTransportCost={calcTransportCost}
            className="antd-control"
            getPopupContainer={getPopupContainer}
            googlePlacesApiKey={googlePlacesApiKey}
            id={`${cssPrefix}-address-1`}
            nameGroupPrefix={nameGroupPrefix}
          />
          <ErrorMessage as={FormErrors} errors={subFormErrors} name={['address1', 'latitude', 'longitude']} />
        </div>
      </div>

      <div className="col-md-12">
        <div className="form-group">
          <label htmlFor={`${cssPrefix}-address-2`}>Suite, buzz code, instructions, etc.</label>
          <input
            ref={register}
            className="form-control"
            data-non-required="true"
            id={`${cssPrefix}-address-2`}
            name={`${nameGroupPrefix}[address2]`}
            placeholder="Suite, buzz code, instructions, etc..."
            type="text"
          />
        </div>
      </div>
      <div className="col-md-12">
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor={`${cssPrefix}-country`}>Country</label>
              <CountrySelector
                className="antd-control"
                getPopupContainer={getPopupContainer}
                id={`${cssPrefix}-country`}
                name={countryFieldName}
              />
              <ErrorMessage as={FormErrors} errors={subFormErrors} name="countryIsoCode" />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor={`${cssPrefix}-state`}>State (optional)</label>
              <StateSelect
                className="antd-control"
                countryFieldName={countryFieldName}
                getPopupContainer={getPopupContainer}
                id={`${cssPrefix}-state`}
                name={`${nameGroupPrefix}[stateIsoCode]`}
              />
              <ErrorMessage as={FormErrors} errors={subFormErrors} name="stateIsoCode" />
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-12">
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor={`${cssPrefix}-city`}>City</label>
              <CitySelect
                calcTransportCost={calcTransportCost}
                className="antd-control"
                getPopupContainer={getPopupContainer}
                googlePlacesApiKey={googlePlacesApiKey}
                id={`${cssPrefix}-city`}
                nameGroupPrefix={nameGroupPrefix}
              />
              <ErrorMessage as={FormErrors} errors={subFormErrors} name={['city', 'cityGooglePlaceId']} />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor={`${cssPrefix}-zip-code`}>{isUSLocation ? 'Zip Code' : 'Postal'}</label>
              <input
                ref={register({ required: `${isUSLocation ? 'Zip code' : 'Postal code'} is required` })}
                className="form-control"
                id={`${cssPrefix}-zip-code`}
                name={`${nameGroupPrefix}[zipCode]`}
                placeholder={isUSLocation ? 'Enter your zip code...' : 'Postal code'}
                type="text"
              />
              <ErrorMessage as={FormErrors} errors={subFormErrors} name="zipCode" />
            </div>
          </div>
        </div>
      </div>
      {/* TODO: Phone and company */}
      {/* <div className="col-md-12">
        <div className="form-group">
          <label htmlFor={`${cssPrefix}-phone`}>Phone (optional)</label>
          <Controller
            as={
              <PhoneInput
                addInternationalOption={false}
                countryOptionsOrder={locationCountryIsoCodes}
                defaultCountry={mainCountryLocationIsoCode}
                id={`${cssPrefix}-phone`}
                numberInputProps={{ className: 'form-control' }}
              />
            }
            control={control}
            name={`${nameGroupPrefix}[phone]`}
          />
        </div>
      </div> */}
    </div>
  )
}

export default memo(AddressFields)
